// StaffingCapacity.tsx — Forward-looking staffing capacity planning dashboard.
// Shows 5-battalion × 4-station × unit matrix, deficit vs target, coverage gaps,
// and historical decline context.

import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from "recharts";
import {
  useStaffingCapacity,
  type UnitStaffing,
  type StaffingGap,
  type StationTotal,
} from "../hooks/useOps";
import ChartCard from "../components/dashboard/ChartCard";
import KPICard from "../components/dashboard/KPICard";
import StatusBadge from "../components/dashboard/StatusBadge";
import { useTheme } from "../hooks/useTheme";
import { getChartTheme } from "../lib/chart-theme";

// ── Constants ─────────────────────────────────────────────────────────────────

const _DCVFD_ACCENT = "#2a7a6c";
const RED = "#dc2626";
const AMBER = "#d97706";
const GREEN = "#16a34a";

// ── Helpers ───────────────────────────────────────────────────────────────────

function statusColor(status: string, isDark: boolean): string {
  if (status === "covered") return isDark ? "#166534" : "#dcfce7";
  if (status === "minimum") return isDark ? "#78350f" : "#fef3c7";
  if (status === "uncovered") return isDark ? "#7f1d1d" : "#fee2e2";
  return isDark ? "#27272a" : "#f4f4f5"; // unknown / unassigned
}

function statusTextColor(status: string, isDark: boolean): string {
  if (status === "covered") return isDark ? "#86efac" : "#166534";
  if (status === "minimum") return isDark ? "#fcd34d" : "#92400e";
  if (status === "uncovered") return isDark ? "#fca5a5" : "#991b1b";
  return isDark ? "#a1a1aa" : "#71717a";
}

function fmt(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

// ── Staffing Matrix ───────────────────────────────────────────────────────────

// Ordered unit list for matrix rows (by station group)
const MATRIX_UNITS = [
  // FS10
  { unit: "E510", station: "FS10", type: "Engine" },
  { unit: "A510", station: "FS10", type: "Ambulance" },
  { unit: "R510", station: "FS10", type: "Rescue" },
  // FS13
  { unit: "E513", station: "FS13", type: "Engine" },
  { unit: "A513", station: "FS13", type: "Ambulance" },
  { unit: "TW513", station: "FS13", type: "Tower" },
  // FS18
  { unit: "E518", station: "FS18", type: "Engine" },
  { unit: "A518", station: "FS18", type: "Ambulance" },
  // FS20
  { unit: "E520", station: "FS20", type: "Engine" },
  { unit: "A520", station: "FS20", type: "Ambulance" },
];

const MIN_STAFF: Record<string, number> = {
  Engine: 3,
  Tower: 3,
  Rescue: 3,
  Ambulance: 2,
};

function StaffingMatrix({
  matrix,
  battalionNames,
  isLoading,
}: {
  matrix: Record<string, Record<string, { assigned: number; status: string }>>;
  battalionNames: string[];
  isLoading: boolean;
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  if (isLoading) {
    return <div className="skeleton rounded-xl" style={{ height: 320 }} />;
  }

  if (battalionNames.length === 0) {
    return (
      <p className="text-sm text-theme-muted text-center py-10">
        No roster data available — run sync to populate staffing data.
      </p>
    );
  }

  const borderColor = isDark ? "#3f3f46" : "#e5e7eb";
  const headerBg = isDark ? "#27272a" : "#f9fafb";

  return (
    <div className="overflow-x-auto">
      <table
        className="w-full text-xs border-collapse"
        style={{ minWidth: 500 }}
      >
        <thead>
          <tr style={{ background: headerBg }}>
            <th
              className="text-left font-semibold text-theme-muted uppercase tracking-wider px-3 py-2.5 sticky left-0 z-10"
              style={{
                background: headerBg,
                minWidth: 100,
                borderRight: `1px solid ${borderColor}`,
              }}
            >
              Unit
            </th>
            <th
              className="text-left font-semibold text-theme-muted uppercase tracking-wider px-3 py-2.5 sticky z-10"
              style={{
                background: headerBg,
                minWidth: 60,
                borderRight: `1px solid ${borderColor}`,
              }}
            >
              Type
            </th>
            <th
              className="text-center font-semibold text-theme-muted uppercase tracking-wider px-3 py-2.5"
              style={{
                background: headerBg,
                minWidth: 48,
                borderRight: `1px solid ${borderColor}`,
              }}
            >
              Min
            </th>
            {battalionNames.map((bn) => (
              <th
                key={bn}
                className="text-center font-semibold text-theme-muted uppercase tracking-wider px-2 py-2.5"
                style={{
                  background: headerBg,
                  minWidth: 80,
                  borderRight: `1px solid ${borderColor}`,
                }}
              >
                {bn}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {MATRIX_UNITS.map(({ unit, station, type }, idx) => {
            const unitData = matrix[unit] ?? {};
            const isLastInStation =
              idx === MATRIX_UNITS.length - 1 ||
              MATRIX_UNITS[idx + 1]?.station !== station;

            return (
              <tr
                key={unit}
                className="transition-colors hover:opacity-80"
                style={{
                  borderBottom: isLastInStation
                    ? `2px solid ${isDark ? "#52525b" : "#d4d4d8"}`
                    : `1px solid ${borderColor}`,
                }}
              >
                {/* Unit label */}
                <td
                  className="px-3 py-2 font-mono font-semibold text-theme-primary sticky left-0 z-10"
                  style={{
                    background: isDark ? "#18181b" : "#fff",
                    borderRight: `1px solid ${borderColor}`,
                  }}
                >
                  <span className="text-xs font-mono">{unit}</span>
                  <span className="ml-1.5 text-xs text-theme-muted font-normal">
                    {station}
                  </span>
                </td>
                {/* Type label */}
                <td
                  className="px-3 py-2 text-theme-secondary"
                  style={{ borderRight: `1px solid ${borderColor}` }}
                >
                  {type}
                </td>
                {/* Min staff */}
                <td
                  className="px-3 py-2 text-center font-mono text-theme-muted"
                  style={{ borderRight: `1px solid ${borderColor}` }}
                >
                  {MIN_STAFF[type] ?? 3}
                </td>
                {/* Battalion cells */}
                {battalionNames.map((bn) => {
                  const cell = unitData[bn];
                  const assigned = cell?.assigned ?? 0;
                  const status = cell?.status ?? "uncovered";
                  const bg = statusColor(status, isDark);
                  const textCol = statusTextColor(status, isDark);
                  return (
                    <td
                      key={bn}
                      className="px-2 py-2 text-center font-mono font-bold"
                      style={{
                        background: bg,
                        color: textCol,
                        borderRight: `1px solid ${borderColor}`,
                      }}
                      title={`${unit} · ${bn}: ${assigned} assigned (${status})`}
                    >
                      {assigned}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 mt-3 px-1 text-xs text-theme-muted">
        {[
          { status: "covered", label: "Covered (≥ min)" },
          { status: "minimum", label: "Below target, at/above 0" },
          { status: "uncovered", label: "Zero / critical gap" },
        ].map(({ status, label }) => (
          <span key={status} className="flex items-center gap-1.5">
            <span
              className="inline-block w-3 h-3 rounded-sm"
              style={{ background: statusColor(status, isDark) }}
            />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Coverage Gaps List ────────────────────────────────────────────────────────

function GapsList({
  gaps,
  isLoading,
}: {
  gaps: StaffingGap[];
  isLoading: boolean;
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="skeleton h-9 rounded-lg" />
        ))}
      </div>
    );
  }

  if (gaps.length === 0) {
    return (
      <p className="text-sm text-theme-muted text-center py-8">
        No coverage gaps detected — all units meet minimum staffing.
      </p>
    );
  }

  return (
    <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
      {gaps.map((gap, i) => (
        <div
          key={i}
          className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm"
          style={{
            background:
              gap.severity === "zero"
                ? isDark
                  ? "#7f1d1d33"
                  : "#fee2e2"
                : isDark
                  ? "#78350f33"
                  : "#fef3c7",
          }}
        >
          {/* Icon */}
          <span
            className="text-base shrink-0"
            style={{ color: gap.severity === "zero" ? RED : AMBER }}
          >
            {gap.severity === "zero" ? "!" : "▼"}
          </span>
          {/* Message */}
          <span
            className="flex-1"
            style={{ color: isDark ? "#f5f5f5" : "#111" }}
          >
            <span className="font-semibold">
              {gap.station} {gap.unit}
            </span>
            {" — "}
            <span className="text-theme-secondary">{gap.battalion}</span>
            {": "}
            <span
              style={{
                color: gap.severity === "zero" ? RED : AMBER,
                fontWeight: 700,
              }}
            >
              {gap.assigned} assigned
            </span>
            {` (need ${gap.minStaff} min)`}
          </span>
          {/* Badge */}
          <span
            className="text-xs font-semibold px-2 py-0.5 rounded-full shrink-0"
            style={{
              background: gap.severity === "zero" ? RED : AMBER,
              color: "#fff",
            }}
          >
            {gap.severity === "zero" ? "ZERO" : "LOW"}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Station Totals Bar Chart ──────────────────────────────────────────────────

function StationTotalsChart({
  stationTotals,
  isLoading,
}: {
  stationTotals: StationTotal[];
  isLoading: boolean;
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const axisColor = isDark ? "#9ca3af" : "#6b7280";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";

  if (isLoading) {
    return <div className="skeleton rounded-xl" style={{ height: 220 }} />;
  }

  if (stationTotals.length === 0) {
    return (
      <p className="text-sm text-theme-muted text-center py-10">
        No station data
      </p>
    );
  }

  const data = stationTotals.map((s) => ({
    station: s.station,
    assigned: s.assigned,
    needed: s.needed,
    gap: Math.max(0, s.needed - s.assigned),
  }));

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart
        data={data}
        margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
        barCategoryGap="20%"
      >
        <CartesianGrid
          vertical={false}
          stroke={gridColor}
          strokeDasharray="3 3"
        />
        <XAxis
          dataKey="station"
          tick={{ fontSize: 12, fill: axisColor }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: axisColor }}
          tickLine={false}
          axisLine={false}
          width={40}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: getChartTheme(isDark).tooltipBg,
            border: `1px solid ${isDark ? "#3f3f46" : "#e5e7eb"}`,
            borderRadius: 8,
            fontSize: 12,
          }}
          formatter={(value, name) => [
            fmt(Number(value)),
            name === "assigned" ? "Assigned members" : "Minimum needed",
          ]}
        />
        <ReferenceLine y={0} stroke={gridColor} />
        <Bar
          dataKey="needed"
          name="needed"
          radius={[4, 4, 0, 0]}
          maxBarSize={48}
          fill={isDark ? "#3f3f46" : "#e5e7eb"}
        />
        <Bar
          dataKey="assigned"
          name="assigned"
          radius={[4, 4, 0, 0]}
          maxBarSize={48}
        >
          {data.map((d, idx) => (
            <Cell
              key={idx}
              fill={
                d.assigned >= d.needed ? GREEN : d.assigned > 0 ? AMBER : RED
              }
              fillOpacity={0.85}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Progress Bar ──────────────────────────────────────────────────────────────

function CapacityBar({
  current,
  minimum,
  target,
}: {
  current: number;
  minimum: number;
  target: number;
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const pctCurrent = Math.min(100, (current / target) * 100);
  const pctMin = Math.min(100, (minimum / target) * 100);

  return (
    <div className="space-y-2">
      {/* Bar */}
      <div
        className="relative rounded-full overflow-hidden"
        style={{ height: 20, background: isDark ? "#3f3f46" : "#e5e7eb" }}
      >
        {/* Current fill */}
        <div
          className="absolute left-0 top-0 h-full rounded-full transition-all"
          style={{
            width: `${pctCurrent}%`,
            background: current >= minimum ? GREEN : RED,
          }}
        />
        {/* Minimum marker */}
        <div
          className="absolute top-0 h-full w-0.5"
          style={{
            left: `${pctMin}%`,
            background: isDark ? "#facc15" : "#ca8a04",
          }}
          title={`Minimum: ${minimum}`}
        />
      </div>
      {/* Labels */}
      <div className="flex items-center justify-between text-xs text-theme-muted">
        <span
          className="font-semibold"
          style={{ color: current >= minimum ? GREEN : RED }}
        >
          {current} Running
        </span>
        <span style={{ color: isDark ? "#facc15" : "#ca8a04" }}>
          Min: {minimum}
        </span>
        <span>Target: {target}</span>
      </div>
    </div>
  );
}

// ── Battalion Summary Cards ───────────────────────────────────────────────────

function BattalionSummaryCards({
  battalions,
  isLoading,
}: {
  battalions: Array<{
    name: string;
    members: number;
    stations: Array<{ station: string; units: UnitStaffing[] }>;
  }>;
  isLoading: boolean;
}) {
  const { resolvedTheme } = useTheme();
  const _isDark = resolvedTheme === "dark";

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="skeleton rounded-xl"
            style={{ height: 120 }}
          />
        ))}
      </div>
    );
  }

  if (battalions.length === 0) {
    return <p className="text-sm text-theme-muted py-4">No battalion data</p>;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
      {battalions.map((bat) => {
        const totalAssigned = bat.stations
          .flatMap((s) => s.units)
          .reduce((s, u) => s + u.assigned, 0);
        const totalMin = bat.stations
          .flatMap((s) => s.units)
          .reduce((s, u) => s + u.minStaff, 0);
        const coveredUnits = bat.stations
          .flatMap((s) => s.units)
          .filter((u) => u.assigned >= u.minStaff).length;
        const totalUnits = bat.stations.flatMap((s) => s.units).length;
        const isHealthy = totalAssigned >= totalMin;

        return (
          <div
            key={bat.name}
            className="glass rounded-xl p-3 flex flex-col gap-2"
            style={{
              borderTop: `3px solid ${isHealthy ? GREEN : RED}`,
            }}
          >
            <div
              className="font-semibold text-sm text-theme-primary truncate"
              title={bat.name}
            >
              {bat.name}
            </div>
            <div className="text-xs text-theme-muted space-y-0.5">
              <div>
                <span className="font-bold text-theme-primary text-lg">
                  {bat.members}
                </span>{" "}
                <span>members</span>
              </div>
              <div
                className="font-medium"
                style={{ color: isHealthy ? GREEN : RED }}
              >
                {totalAssigned}/{totalMin} min seats
              </div>
              <div className="text-theme-muted">
                {coveredUnits}/{totalUnits} units covered
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Recommendations List ──────────────────────────────────────────────────────

function RecommendationsList({
  items,
  isLoading,
}: {
  items: string[];
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="skeleton h-8 rounded-lg" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <p className="text-sm text-theme-muted">No recommendations generated.</p>
    );
  }

  return (
    <ul className="space-y-2">
      {items.map((rec, i) => (
        <li
          key={i}
          className="flex items-start gap-2 text-sm text-theme-secondary"
        >
          <span className="mt-0.5 shrink-0 text-status-amber font-bold">→</span>
          <span>{rec}</span>
        </li>
      ))}
    </ul>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function StaffingCapacity() {
  const { data, isLoading } = useStaffingCapacity();

  const targets = data?.targets ?? {
    minimum: 130,
    withBuffer: 169,
    currentRunning: 0,
  };
  const missingStatus = data?.rosterMissingStatus ?? 0;
  const battalions = data?.battalions ?? [];
  const matrix = data?.matrix ?? {};
  const gaps = data?.gaps ?? [];
  const stationTotals = data?.stationTotals ?? [];
  const recommendations = data?.recommendations ?? [];

  const battalionNames = useMemo(
    () => battalions.map((b) => b.name),
    [battalions],
  );

  const deficitVsTarget = targets.currentRunning - targets.withBuffer;
  const deficitVsMin = targets.currentRunning - targets.minimum;
  const pctOfTarget = Math.round(
    (targets.currentRunning / targets.withBuffer) * 100,
  );

  // Historical-context block is illustrative. The ~2010 figure is an estimate;
  // the rest derive from the live current count so the narrative stays honest.
  const EST_2010_RUNNING = 150;
  const declineVs2010 = EST_2010_RUNNING - targets.currentRunning;
  const needToMin = Math.max(0, targets.minimum - targets.currentRunning);
  const needToTarget = Math.max(0, targets.withBuffer - targets.currentRunning);

  const criticalGaps = gaps.filter((g) => g.severity === "zero").length;

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 md:space-y-8 overflow-x-hidden">
      {/* ── Page header ─────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-theme-primary">
            Staffing Capacity
          </h1>
          <p className="text-theme-secondary mt-1 text-sm">
            5 battalions × 4 stations × apparatus — forward-looking capacity
            planning
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {missingStatus > 0 ? (
            <StatusBadge label="Partial roster data" variant="yellow" />
          ) : (
            <StatusBadge label="Live data" variant="green" />
          )}
          <StatusBadge label="5 battalions" variant="blue" />
        </div>
      </div>

      {/* ── KPI Row ──────────────────────────────────────────────────── */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          label="Running Members"
          value={fmt(targets.currentRunning)}
          subtitle={
            missingStatus > 0
              ? `${fmt(targets.currentRunning)} on roster with status "Running" · ${fmt(missingStatus)} rows missing status (floor)`
              : `${fmt(targets.currentRunning)} members actively staffing apparatus`
          }
          accent="amber"
          isLoading={isLoading}
        />
        <KPICard
          label="Minimum Target"
          value={fmt(targets.minimum)}
          subtitle={`26 seats/battalion × 5 = ${fmt(targets.minimum)}`}
          accent="neutral"
          isLoading={isLoading}
        />
        <KPICard
          label="With 130% Buffer"
          value={fmt(targets.withBuffer)}
          subtitle="Accounts for leave, illness, concurrent needs"
          accent="neutral"
          isLoading={isLoading}
        />
        <KPICard
          label="Deficit"
          value={fmt(deficitVsTarget)}
          subtitle={`${fmt(deficitVsMin)} below minimum · ${pctOfTarget}% of target`}
          accent="red"
          trend={{
            direction: "down",
            label: `${Math.abs(deficitVsTarget)} short of buffer`,
            positiveIsGood: true,
          }}
          isLoading={isLoading}
        />
      </section>

      {/* ── Capacity Progress Bar ─────────────────────────────────────── */}
      <ChartCard
        title="Capacity vs Target"
        subtitle={`${targets.currentRunning} running / ${targets.minimum} minimum / ${targets.withBuffer} with 130% buffer`}
        minHeight={0}
      >
        <div className="px-1">
          <CapacityBar
            current={targets.currentRunning}
            minimum={targets.minimum}
            target={targets.withBuffer}
          />
          <p className="mt-3 text-xs text-theme-muted">
            The yellow marker shows the minimum threshold (130 members needed to
            staff all units every night). The target bar (169) applies a 30%
            buffer for leave, illness, and concurrent responses.
          </p>
        </div>
      </ChartCard>

      {/* ── Historical Decline Callout ────────────────────────────────── */}
      <div className="glass rounded-2xl p-5 border-l-4 border-status-red">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <h2 className="text-sm font-bold text-theme-primary uppercase tracking-wider">
            Operational Decline — Historical Context
          </h2>
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-theme-muted/15 text-theme-muted">
            Illustrative — narrative, 2010 figure is an estimate
          </span>
        </div>
        <blockquote className="text-sm text-theme-secondary leading-relaxed">
          "In 2010, DCVFD could staff most units every night across all 5
          battalions. Today we're lucky to cover Engine + transport at FS10 and
          FS13 nightly. FS18 and FS20 go uncovered regularly."
        </blockquote>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <div
            className="rounded-lg p-3"
            style={{ background: "rgba(22,163,74,0.1)" }}
          >
            <div className="font-bold text-status-green text-base">~150</div>
            <div className="text-theme-muted mt-0.5">
              Running members ca. 2010 (est.)
            </div>
            <div className="text-theme-muted">
              FS10, FS13, FS18, FS20 mostly covered nightly
            </div>
          </div>
          <div
            className="rounded-lg p-3"
            style={{ background: "rgba(220,38,38,0.1)" }}
          >
            <div className="font-bold text-status-red text-base">
              {fmt(targets.currentRunning)}
            </div>
            <div className="text-theme-muted mt-0.5">
              Running members today (2026)
            </div>
            <div className="text-theme-muted">
              FS18 + FS20 often uncovered; coverage fragile at FS10/FS13
            </div>
          </div>
          <div
            className="rounded-lg p-3"
            style={{ background: "rgba(217,119,6,0.1)" }}
          >
            <div className="font-bold text-status-amber text-base">
              −{fmt(declineVs2010)}
            </div>
            <div className="text-theme-muted mt-0.5">
              Running member decline vs 2010 (est.)
            </div>
            <div className="text-theme-muted">
              Need +{fmt(needToMin)} to hit minimum · +{fmt(needToTarget)} to
              reach safe target
            </div>
          </div>
        </div>
      </div>

      {/* ── Battalion Overview Cards ──────────────────────────────────── */}
      <ChartCard
        title="Battalion Overview"
        subtitle="Current assigned members and unit coverage per battalion"
        minHeight={0}
      >
        <BattalionSummaryCards battalions={battalions} isLoading={isLoading} />
      </ChartCard>

      {/* ── Staffing Matrix ───────────────────────────────────────────── */}
      <ChartCard
        title="Staffing Matrix"
        subtitle="Units (rows) × Battalions (columns) — assigned seat count, colour-coded by coverage status"
        minHeight={0}
      >
        <StaffingMatrix
          matrix={matrix}
          battalionNames={battalionNames}
          isLoading={isLoading}
        />
      </ChartCard>

      {/* ── Coverage Gaps ─────────────────────────────────────────────── */}
      <ChartCard
        title="Coverage Gaps"
        subtitle={`${gaps.length} unit × battalion combinations below minimum staffing · ${criticalGaps} zero-staffed`}
        headerRight={
          gaps.length > 0 ? (
            <span
              className="text-xs font-bold px-2 py-1 rounded-full"
              style={{
                background: criticalGaps > 0 ? RED : AMBER,
                color: "#fff",
              }}
            >
              {criticalGaps} Critical
            </span>
          ) : undefined
        }
        minHeight={0}
      >
        <GapsList gaps={gaps} isLoading={isLoading} />
      </ChartCard>

      {/* ── Station Totals ─────────────────────────────────────────────── */}
      <ChartCard
        title="Priority Stations"
        subtitle="Total assigned vs minimum needed across all 5 battalions — grey = minimum needed, colour = assigned"
      >
        <StationTotalsChart
          stationTotals={stationTotals}
          isLoading={isLoading}
        />
        {!isLoading && stationTotals.length > 0 && (
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            {stationTotals.map((s) => (
              <div key={s.station} className="text-center">
                <div className="font-bold text-theme-primary">{s.station}</div>
                <div
                  className="font-semibold"
                  style={{
                    color:
                      s.assigned >= s.needed
                        ? GREEN
                        : s.assigned > 0
                          ? AMBER
                          : RED,
                  }}
                >
                  {s.assigned} / {s.needed}
                </div>
                <div className="text-theme-muted">assigned / needed</div>
              </div>
            ))}
          </div>
        )}
      </ChartCard>

      {/* ── Recommendations ───────────────────────────────────────────── */}
      <ChartCard
        title="Recommendations"
        subtitle="Auto-generated insights based on current roster data"
        minHeight={0}
      >
        <RecommendationsList items={recommendations} isLoading={isLoading} />
        <p className="mt-4 text-xs text-theme-muted">
          Staffing math: 4 stations × (Engine min 3 + Ambulance min 2) + Tower
          513 (3) + Rescue 510 (3) = 26 seats per battalion × 5 = 130 minimum.
          Apply 130% buffer (leave/illness) = 169 members needed for safe
          operational coverage.
        </p>
      </ChartCard>
    </div>
  );
}
